import styled from "@emotion/styled";
import { Avatar, ButtonBase } from "@mui/material";

import {
  collapseTransition,
  SIDEBAR_RAIL_TILE
} from "../MainSidebar.components";

const openProp = {
  shouldForwardProp: (prop: PropertyKey) => prop !== "open"
};
const sizeProp = {
  shouldForwardProp: (prop: PropertyKey) => prop !== "size"
};
const highlightedProp = {
  shouldForwardProp: (prop: PropertyKey) => prop !== "highlighted"
};

const ELLIPSIS = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap"
} as const;

const PROFILE_CARD_HEIGHT = 40;
const PROFILE_FOOTER_PADDING_TOP = 14;
const PROFILE_FOOTER_RULE_GAP = 6;

const ProfileFooter = styled("div")({
  alignItems: "center",
  alignSelf: "stretch",
  display: "flex",
  flex: "none",
  flexDirection: "column",
  marginTop: "auto",
  paddingTop: PROFILE_FOOTER_PADDING_TOP
});

const ProfileFooterRule = styled(
  "div",
  openProp
)<{ open: boolean }>(({ open, theme }) => ({
  alignSelf: "center",
  backgroundColor: theme.vars.ui.sidebar.nav.line,
  flex: "none",
  height: 1,
  marginBottom: PROFILE_FOOTER_RULE_GAP,
  width: open ? "calc(100% - 6px)" : SIDEBAR_RAIL_TILE
}));

const ProfileAnchor = styled("div")({
  alignItems: "center",
  alignSelf: "stretch",
  display: "flex",
  flexDirection: "column",
  position: "relative"
});

const ProfileCard = styled(ButtonBase, {
  shouldForwardProp: (prop: PropertyKey) => prop !== "expanded"
})<{ expanded: boolean }>(({ expanded, theme }) => ({
  "&:hover": {
    backgroundColor: expanded ? theme.vars.ui.sidebar.nav.hover : "transparent"
  },
  alignItems: "center",
  borderRadius: expanded ? 10 : 99,
  boxSizing: "border-box",
  color: theme.vars.palette.text.primary,
  cursor: "pointer",
  display: "flex",
  flex: "none",
  gap: expanded ? 10 : 0,
  height: PROFILE_CARD_HEIGHT,
  justifyContent: expanded ? "flex-start" : "center",
  padding: expanded ? "4px 8px 4px 6px" : 0,
  textAlign: "left",
  transition: [
    collapseTransition(theme, ["gap", "padding", "width", "border-radius"]),
    theme.transitions.create("background-color", {
      duration: theme.transitions.duration.shortest
    })
  ].join(", "),
  width: expanded ? "100%" : SIDEBAR_RAIL_TILE
}));

const ProfileAvatarWrap = styled(
  "span",
  sizeProp
)<{ size: number }>(({ size }) => ({
  display: "block",
  flex: "none",
  height: size,
  position: "relative",
  width: size
}));

const ProfileAvatar = styled(
  Avatar,
  sizeProp
)<{ size: number }>(({ size, theme }) => ({
  backgroundColor: theme.vars.palette.background.default,
  boxShadow: theme.vars.appEffects.shadow.avatar,
  color: theme.vars.palette.text.secondary,
  height: size,
  img: {
    objectFit: "cover"
  },
  width: size
}));

const ProfileStatusDot = styled("span")(({ theme }) => ({
  backgroundColor: "var(--mui-palette-primary-glow)",
  borderRadius: 99,
  bottom: -1,
  boxShadow: `0 0 0 2px ${theme.vars.ui.sidebar.nav.background}`,
  height: 9,
  position: "absolute",
  right: -1,
  width: 9
}));

const ProfileName = styled("span")(({ theme }) => ({
  ...ELLIPSIS,
  fontSize: theme.typography.body2.fontSize,
  fontWeight: theme.typography.fontWeightMedium,
  lineHeight: 1.25
}));

const ProfileOrg = styled("span")(({ theme }) => ({
  ...ELLIPSIS,
  color: "rgba(var(--mui-palette-text-primaryChannel) / 0.6)",
  fontSize: theme.typography.caption.fontSize,
  lineHeight: 1.25
}));

const ProfileMenu = styled("div")({
  bottom: "calc(100% + 8px)",
  display: "flex",
  flexDirection: "column",
  gap: 4,
  left: 0,
  position: "absolute",
  right: 0,
  zIndex: 3
});

const ProfileMenuItem = styled(
  ButtonBase,
  highlightedProp
)<{ highlighted: boolean }>(({ highlighted, theme }) => ({
  "&:hover": {
    backgroundColor: theme.vars.ui.sidebar.card.hover
  },
  alignItems: "center",
  backgroundColor: highlighted
    ? theme.vars.ui.sidebar.item.selected.background
    : theme.vars.ui.sidebar.card.background,
  border: `1px solid ${
    highlighted
      ? theme.vars.ui.sidebar.item.selected.ring
      : theme.vars.ui.sidebar.line
  }`,
  borderRadius: 10,
  boxShadow: "0 1px 4px rgba(3, 26, 22, 0.06)",
  boxSizing: "border-box",
  color: highlighted
    ? theme.vars.ui.sidebar.item.selected.text
    : theme.vars.ui.sidebar.item.text,
  display: "flex",
  fontSize: theme.typography.body2.fontSize,
  fontWeight: theme.typography.fontWeightMedium,
  gap: 10,
  height: 36,
  justifyContent: "flex-start",
  lineHeight: 1,
  padding: "0 10px",
  textAlign: "left",
  transition: theme.transitions.create("background-color", {
    duration: theme.transitions.duration.shortest
  }),
  width: "100%"
}));

const ProfileMenuIcon = styled(
  "span",
  highlightedProp
)<{ highlighted: boolean }>(({ highlighted }) => ({
  color: highlighted
    ? "var(--mui-palette-text-primary)"
    : "rgba(var(--mui-palette-text-primaryChannel) / 0.55)",
  display: "inline-flex",
  flex: "none"
}));

const ProfileMenuLabel = styled("span")({
  ...ELLIPSIS,
  flex: 1,
  lineHeight: 1.25
});

const ProfileMenuBadge = styled("span")({
  backgroundColor: "var(--mui-palette-primary-glow)",
  borderRadius: 99,
  flex: "none",
  height: 8,
  width: 8
});

const ProfileRailMenu = styled("div")({
  alignItems: "center",
  bottom: "calc(100% + 10px)",
  display: "flex",
  flexDirection: "column",
  gap: 8,
  left: "50%",
  position: "absolute",
  transform: "translateX(-50%)",
  zIndex: 3
});

const ProfileRailMenuButton = styled(
  ButtonBase,
  highlightedProp
)<{ highlighted: boolean }>(({ highlighted, theme }) => ({
  "&:hover": {
    backgroundColor: theme.vars.ui.sidebar.card.hover
  },
  backgroundColor: highlighted
    ? theme.vars.ui.sidebar.item.selected.background
    : theme.vars.ui.sidebar.card.background,
  border: `1px solid ${
    highlighted
      ? theme.vars.ui.sidebar.item.selected.ring
      : theme.vars.ui.sidebar.line
  }`,
  borderRadius: 99,
  boxShadow: "0 2px 10px rgba(3, 26, 22, 0.14)",
  color: highlighted
    ? theme.vars.ui.sidebar.item.selected.text
    : theme.vars.palette.text.primary,
  flex: "none",
  height: 36,
  position: "relative",
  transition: theme.transitions.create("background-color", {
    duration: theme.transitions.duration.shortest
  }),
  width: 36
}));

const ProfileRailMenuBadge = styled(ProfileMenuBadge)(({ theme }) => ({
  boxShadow: `0 0 0 2px ${theme.vars.ui.sidebar.card.background}`,
  position: "absolute",
  right: 2,
  top: 2
}));

export {
  ProfileAnchor,
  ProfileAvatar,
  ProfileAvatarWrap,
  ProfileCard,
  ProfileFooter,
  ProfileFooterRule,
  ProfileMenu,
  ProfileMenuBadge,
  ProfileMenuIcon,
  ProfileMenuItem,
  ProfileMenuLabel,
  ProfileName,
  ProfileOrg,
  ProfileRailMenu,
  ProfileRailMenuBadge,
  ProfileRailMenuButton,
  ProfileStatusDot
};
